// Carregamento com cards ("esqueleto") — o mesmo jeito em todas as telas do
// painel (dono, 25/10/2026): enquanto os dados chegam na primeira abertura, a
// tela já mostra o formato do que vem (cards, lista, tabela ou grade) piscando,
// no lugar de spinner solto ou de "Nenhum item" aparecendo antes da hora.
//
// Regra de uso: título, botões e filtros aparecem na hora; só a área de dados
// vira esqueleto, e só na PRIMEIRA carga (recarregar depois de salvar não pisca
// a tela). Cada peça aceita `className` para encaixar no layout da tela.

import { Skeleton } from "../ui/skeleton";

const repetir = (n, fn) => Array.from({ length: n }, (_, i) => fn(i));

/** Fileira de cards de número (resumos do topo). */
export function CarregandoCards({ quantos = 4, className = "grid grid-cols-2 lg:grid-cols-4 gap-3", altura = "h-[104px]" }) {
  return (
    <div className={className} data-e2e="carregando-cards" aria-busy="true" aria-label="Carregando">
      {repetir(quantos, (i) => (
        <div key={i} className={`rounded-2xl border bg-card p-4 flex flex-col gap-2.5 ${altura}`}>
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      ))}
    </div>
  );
}

/** Lista de linhas (avatar/ícone, duas linhas de texto e valores à direita). */
export function CarregandoLista({ linhas = 8, avatar = true, colunas = 3, className = "rounded-2xl border bg-card overflow-hidden" }) {
  return (
    <div className={className} data-e2e="carregando-lista" aria-busy="true" aria-label="Carregando">
      <div className="divide-y">
        {repetir(linhas, (i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            {avatar && <Skeleton className="w-10 h-10 rounded-full shrink-0" />}
            <div className="flex flex-col gap-1.5 flex-1 min-w-0">
              <Skeleton className="h-4 w-48 max-w-full" />
              <Skeleton className="h-3 w-32 max-w-full" />
            </div>
            {repetir(colunas, (j) => <Skeleton key={j} className={`h-4 w-20 shrink-0 ${j > 0 ? "hidden md:block" : "hidden sm:block"}`} />)}
            <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Tabela: cabeçalho e linhas com N colunas. */
export function CarregandoTabela({ linhas = 8, colunas = 5, className = "rounded-2xl border bg-card overflow-hidden" }) {
  const grade = { gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` };
  return (
    <div className={className} data-e2e="carregando-tabela" aria-busy="true" aria-label="Carregando">
      <div className="grid gap-4 px-4 py-3 border-b bg-muted/40" style={grade}>
        {repetir(colunas, (j) => <Skeleton key={j} className="h-3 w-2/3" />)}
      </div>
      <div className="divide-y">
        {repetir(linhas, (i) => (
          <div key={i} className="grid gap-4 px-4 py-3.5" style={grade}>
            {repetir(colunas, (j) => <Skeleton key={j} className={`h-4 ${j === 0 ? "w-4/5" : "w-1/2"}`} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Grade de cards grandes (produtos, mesas, campanhas, cartões de configuração). */
export function CarregandoGrade({ quantos = 8, className = "grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4", altura = "h-[180px]", comImagem = false }) {
  return (
    <div className={className} data-e2e="carregando-grade" aria-busy="true" aria-label="Carregando">
      {repetir(quantos, (i) => (
        <div key={i} className={`rounded-2xl border bg-card p-3 flex flex-col gap-2.5 ${comImagem ? "" : altura}`}>
          {comImagem && <Skeleton className="aspect-square w-full rounded-xl" />}
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <div className="mt-auto flex gap-2"><Skeleton className="h-8 flex-1 rounded-lg" /><Skeleton className="h-8 w-8 rounded-lg" /></div>
        </div>
      ))}
    </div>
  );
}

/** Bloco livre (gráfico, painel, formulário): um card com altura dada. */
export function CarregandoBloco({ altura = "h-64", className = "" }) {
  return (
    <div className={`rounded-2xl border bg-card p-4 flex flex-col gap-3 ${altura} ${className}`} data-e2e="carregando-bloco" aria-busy="true" aria-label="Carregando">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="flex-1 w-full rounded-xl" />
    </div>
  );
}

/** Formulário de configuração: rótulos e campos. */
export function CarregandoFormulario({ campos = 6, className = "rounded-2xl border bg-card p-5 space-y-5" }) {
  return (
    <div className={className} data-e2e="carregando-formulario" aria-busy="true" aria-label="Carregando">
      {repetir(campos, (i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}
